import {
  CalendarToday as CalendarTodayIcon,
  InfoOutlined as InfoOutlinedIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';
import { get, isEmpty } from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChipWithCustomTooltip } from 'src/components/chipWithCustomTooltip/ChipWithCustomTooltip';
import { FilterList } from 'src/components/filterList';
import { ActionBar, Header } from 'src/components/header';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import MainPane from 'src/components/TwoPaneLayout/MainPane';
import SidePane from 'src/components/TwoPaneLayout/SidePane';
import TwoPaneLayout from 'src/components/TwoPaneLayout/TwoPaneLayout';
import { OO_LOCATIONS_PAGE_SIZE } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting } from 'src/hooks';
import { useSearchFilter } from 'src/hooks/useSearchFilter';
import { IFilter, ILocationListing } from 'src/interfaces';
import { lang } from 'src/utils';

import { ChipSpacer } from '../detailLocationPage/DetailLocationPage.styled';
import { LocationListTable } from './components/LocationListTable';
import { useLocationTab } from './hooks/useLocationTab.hook';
import { ActionsContainer } from './LocationListPage.styled';

export const LocationListPageOO: React.FC = () => {
  const {
    locations,
    isLocationTabDataLoading,
    totalCount,
    totalPages,
    activeFilters,
    handlePageChange,
    onSortChangeHandle,
    selectedSortOption,
    page,
    rowsPerPage,
    getParamsValue,
    onRowsPerPageChange,
    locationsContainOutdoorType,
    searchParams,
  } = useLocationTab({ defaultPageSize: OO_LOCATIONS_PAGE_SIZE });
  const navigate = useNavigate();
  const { handleOnChangeFilterKeyword, handleOnRemoveFilterKeyword } =
    useSearchFilter();
  const [isStoreNumberFuzzySearch, setIsStoreNumberFuzzySearch] = useState(
    !('store_number.list' in activeFilters),
  );

  const handleEditCODMessaging = () => {
    navigate({
      pathname: PATHS.LOCATIONS_BULK_EDIT_MESSAGING,
    });
  };

  const getBannerChecked = (row: ILocationListing) => {
    const config = row.config;

    if (!config || (typeof config === 'object' && isEmpty(config))) {
      return true;
    }

    const bannerActive = get(
      config,
      'cod.confirmSetting.header.review.banner.active',
    );

    return typeof bannerActive === 'boolean' ? bannerActive : true;
  };

  const getMessagingChecked = (row: ILocationListing) => {
    const config = row.config;

    if (!config || (typeof config === 'object' && isEmpty(config))) {
      return true;
    }

    const hideCandidates: Array<boolean | undefined> = [
      get(config, 'cod.codSetting.topMessage.hideRmhcMessage'),
      get(config, 'cod.confirmSetting.topMessage.hideRmhcMessage'),
    ];

    const hideValues = hideCandidates.filter(
      (value): value is boolean => typeof value === 'boolean',
    );

    if (hideValues.length === 0) {
      return true;
    }

    return hideValues.every((hideValue) => !hideValue);
  };

  const onStoreNumberSearchTypeChange = () => {
    if (isStoreNumberFuzzySearch) {
      setIsStoreNumberFuzzySearch(false);
      onChangeToExactMatch('store_number.regexp', 'store_number.list');
    } else {
      setIsStoreNumberFuzzySearch(true);
      onChangeToFuzzyMatch('store_number.list', 'store_number.regexp');
    }
  };

  const filters: IFilter[] = [
    {
      labelOverride: lang.ownerOperator.ownerOperatorTags,
      label: lang.locationOwnerTagsName,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'success',
      },
    },
    {
      label: lang.zone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'success',
      },
    },
    {
      label: lang.subZone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'success',
      },
    },
    {
      labelOverride: 'Store number',
      label: 'store_number',
      isLoading: false,
      options: [],
      fieldType: 'freeSearch',
      hasExactSearch: true,
      isFuzzySearch: isStoreNumberFuzzySearch,
      onSearchTypeChange: onStoreNumberSearchTypeChange,
      filterSearchVal: getParamsValue(
        isStoreNumberFuzzySearch ? 'store_number.regexp' : 'store_number.list',
      ),
      onChangeFilterKeyword: (value: string) =>
        handleOnChangeFilterKeyword(
          value,
          isStoreNumberFuzzySearch
            ? 'store_number.regexp'
            : 'store_number.list',
        ),
      onRemoveFilterKeyword: (value: string) =>
        handleOnRemoveFilterKeyword(
          value,
          isStoreNumberFuzzySearch
            ? 'store_number.regexp'
            : 'store_number.list',
        ),
    },
  ];

  const [isFuzzySearch, setIsFuzzySearch] = useState(
    !('name.list' in activeFilters),
  );
  const {
    onChangeKeyword,
    onRemoveKeyword,
    onChangeFilter,
    onChangeParams,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
    onReset,
  } = useFilterAndSorting(filters, [], isFuzzySearch, setIsFuzzySearch);

  const onResetFilters = () => {
    onReset();
    onChangeParams('store_number.list', []);
    onChangeParams('store_number.regexp', []);
    setIsStoreNumberFuzzySearch(true);
  };

  const onSearchTypeChange = () => {
    if (isFuzzySearch) {
      onChangeToExactMatch('name.regexp', 'name.list');
      setIsFuzzySearch(false);
    } else {
      onChangeToFuzzyMatch('name.list', 'name.regexp');
      setIsFuzzySearch(true);
    }
  };

  return (
    <Grid>
      <Header
        actionBar={
          <ActionBar
            title={lang.locations}
            type={lang.listLocationsPageOO}
            locationCodes={locations.map((location) => location.location_code)}
            onEditCODMessaging={handleEditCODMessaging}
            numOfSelected={locations.length}
          />
        }
        title={lang.locations}
      />
      <TwoPaneLayout>
        <SidePane>
          <FilterList
            enableSearchArea
            filters={filters}
            onChangeFilter={onChangeFilter}
            onRemoveKeyword={onRemoveKeyword}
            onChangeKeyword={onChangeKeyword}
            onReset={onResetFilters}
            showReset
            hasExactSearch
            onSearchTypeChange={onSearchTypeChange}
            isFuzzySearch={isFuzzySearch}
          />
        </SidePane>

        <MainPane>
          <LocationListTable
            rows={locations}
            itemsToShowCheckbox={locationsContainOutdoorType}
            loading={isLocationTabDataLoading}
            selectedSortOption={selectedSortOption}
            onSortChange={onSortChangeHandle}
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            getBannerChecked={getBannerChecked}
            getMessagingChecked={getMessagingChecked}
            renderActions={(row: ILocationListing) => (
              <ActionsContainer>
                <CustomTooltip
                  title={lang.locationDetails}
                  placement="top"
                  skipValidateOverflow={true}>
                  <IconButton
                    aria-label="clock"
                    onClick={() =>
                      navigate({
                        pathname: PATHS.LOCATION_DETAILS(
                          row.location_code.toString(),
                        ),
                        search: searchParams,
                      })
                    }>
                    <InfoOutlinedIcon />
                  </IconButton>
                </CustomTooltip>
                <CustomTooltip
                  title={lang.locationSchedule}
                  placement="top"
                  skipValidateOverflow={true}>
                  <IconButton
                    aria-label="calendar"
                    onClick={() =>
                      navigate({
                        pathname: PATHS.LOCATION_CALENDAR(
                          row.location_code.toString(),
                        ),
                        search: searchParams,
                      })
                    }>
                    <CalendarTodayIcon />
                  </IconButton>
                </CustomTooltip>
                <CustomTooltip
                  title={lang.locationTimings}
                  placement="top"
                  skipValidateOverflow={true}>
                  <IconButton
                    aria-label="clock"
                    onClick={() =>
                      navigate({
                        pathname: PATHS.LOCATION_TIMINGS(
                          row.location_code.toString(),
                        ),
                        search: searchParams,
                      })
                    }>
                    <ScheduleIcon />
                  </IconButton>
                </CustomTooltip>
              </ActionsContainer>
            )}
            renderTags={(row: ILocationListing) => {
              const MAX_VISIBLE = 2;
              const location_owner_tags = row.locationOwnerTags || [];
              const hidden = location_owner_tags.length - MAX_VISIBLE;

              return (
                <div>
                  {location_owner_tags
                    .slice(0, MAX_VISIBLE)
                    .map((tag: { name: string }, i: number) => (
                      <ChipSpacer key={i}>
                        <ChipWithCustomTooltip
                          name={lang.locationTags}
                          chipName={tag?.name}
                          tooltipTitle={tag?.name}
                          tooltipWidth={140}
                          color="success"
                          tooltipActivationWidth={62}
                          tooltipPlacement="bottom"
                        />
                      </ChipSpacer>
                    ))}
                  {hidden > 0 && (
                    <ChipSpacer>
                      <ChipWithCustomTooltip
                        name={lang.locationTags}
                        chipName={`+${hidden}`}
                        tooltipTitle={`+${hidden}`}
                        tooltipWidth={90}
                        color="success"
                        tooltipActivationWidth={70}
                        tooltipPlacement="right"
                      />
                    </ChipSpacer>
                  )}
                </div>
              );
            }}
          />
        </MainPane>
      </TwoPaneLayout>
    </Grid>
  );
};
